import { useContext, useRef } from 'react'
import { kRouteSegments } from '@/routing/paths'
import ErrorComponent from '@/components/error/error-component'
import { useAppDispatch, useAppSelector } from '@/store/store-hooks'
import { useParams, Link } from 'react-router-dom'
import { RootState } from '@/store/store'
import { useEffect, useState } from 'react'
import { CircularProgress } from '@mui/material'

import ContentViewerColumn from '@/content-viewer/components/ContentViewerColumn'
import ContentViewerSlideover from '@/content-viewer/components/ContentViewerSlideover'
import {
  selectAgentConversationActiveContentViewer,
  selectAgentConversationExists,
  selectMatterConversationsInitialLoadComplete,
} from '@/agent/conversations/store/selectors'
import AgentChatWindow from '@/agent/chat_window/AgentChatWindow'
import { ContentViewerActions } from '@/content-viewer/store/slice'
import { selectIsContentViewerOpen } from '@/content-viewer/store/selectors'
import { CSSTransition } from 'react-transition-group'
import { setActiveConversationId } from '@/chat-common/store/chat-ux.slice'
import { useViewportSizeInPixels } from '@/hooks/use-viewport-size-in-pixels'
import { ChatBreakpoints } from './chat-layout-constants'

import { useConversationNavigation } from '@/chat-common/conversation-navigation/hooks/useConversationNavigation'
import { kMattersRoute } from '@/matter/constants'
import { selectMatterById } from '@/matter/store/selectors'
import { isUnassignedMatter } from '@/matter/utils/is-unassigned-matter'
import { selectHasConversationsForMatter } from '@/chat-common/conversation_list/selectors'
import { AgentConversationsActions } from '@/agent/conversations/store/slice'
import { AuthContext } from '@/context/auth-context'

/**
 * Chat Layout
 * Main template page for the UX structure of chat-based interfaces.
 *
 * This supports agent chats in the dashboard runtime.
 * @returns
 */
export default function ChatLayout() {
  const { chatFeature, chatId, matterId: urlMatterId } = useParams()
  // Matter id from the URL params (source of truth before MatterLayout hydrates Redux)
  const urlDerivedMatterId = urlMatterId ?? null
  const dispatch = useAppDispatch()
  const { navigateToConversationByMatter, handleMatterChangeNavigation } = useConversationNavigation()
  const { user } = useContext(AuthContext)
  const userIsAnon = user?.isAnonymous

  // Fetch agent conversations here rather than relying on ChatListSidebar — the sidebar can be hidden
  // or unmounted (e.g. unassigned matter empty state). No duplicate fetching: when the sidebar
  // is present it also dispatches, but forceRefresh: false makes the second call a no-op.
  useEffect(() => {
    if (userIsAnon) return
    dispatch(AgentConversationsActions.refreshMatterConversationList({ matterId: urlDerivedMatterId, forceRefresh: false }))
  }, [userIsAnon, urlDerivedMatterId, dispatch])

  // Local state for showing a 404 error if no chat is found from the chat id in the URL
  const [show404, setShow404] = useState(false)

  // Column width ref
  const referenceColumnRef = useRef<HTMLDivElement | null>(null)

  // Determine the screen size based on the viewport width and the component's breakpoints
  const { width: viewportWidth } = useViewportSizeInPixels()
  const screenSize = viewportWidth > ChatBreakpoints.SMALL ? ChatBreakpoints.LARGE : ChatBreakpoints.SMALL

  const isValidChatFeature: boolean = chatFeature === kRouteSegments.assistant

  const matterConversationsInitialLoadComplete = useAppSelector((state: RootState) =>
    selectMatterConversationsInitialLoadComplete(state, { matterId: urlDerivedMatterId })
  )
  const mattersInitialLoadComplete = useAppSelector((state: RootState) => state.mattersState.initialLoadComplete)
  // Whether the initial load has been completed for the matter list and the current matter's conversations
  const initialLoadComplete = matterConversationsInitialLoadComplete && mattersInitialLoadComplete

  // Dashboard runtime only mounts agent conversations
  const isAgentConversation = useAppSelector((state: RootState) => selectAgentConversationExists(state, { conversationId: chatId }))

  // Determine whether the runtime conversation is supported once the lists have loaded
  const showAgentChat = isAgentConversation && matterConversationsInitialLoadComplete

  // Any non-agent conversation ID is unsupported in the dashboard runtime
  const isInvalidChatId = !!chatId && initialLoadComplete && !showAgentChat

  // Get the active matter id, and a ref to the previous active matter id (for tracking when it changes)
  // Initialized to `undefined` so the first hydration from MatterLayout is recorded without triggering navigation
  const activeMatterId = useAppSelector((state: RootState) => state.mattersState.activeMatterId)
  const prevActiveMatterId = useRef<string | null | undefined>(undefined)

  // Check if the content viewer for agent conversations (to display a reference or artifact) is visible
  const showContentViewerPane = useAppSelector((state: RootState) => selectIsContentViewerOpen(state))

  // Check if there is an active content viewer for the agent conversation, used to sync the content viewer state when switching between conversations
  const AgentConversationActiveContentViewer = useAppSelector((state: RootState) =>
    selectAgentConversationActiveContentViewer(state, { conversationId: chatId })
  )

  const urlMatter = useAppSelector((state: RootState) => selectMatterById(state, { matterId: urlDerivedMatterId }))
  const hasConversationsForMatter = useAppSelector((state: RootState) =>
    selectHasConversationsForMatter(state, { matterId: urlDerivedMatterId, currentUserId: user?.uid })
  )

  /**
   * If there is no chatId in the URL, navigate to the appropriate conversation for the feature and matter.
   *
   * Used on first landing into a feature without a specific conversation id.
   */
  useEffect(() => {
    if (
      !chatId && // No chatId in the URL
      isValidChatFeature && // Valid chat feature in the URL
      initialLoadComplete // The matter's conversations and the matter list are loaded
    ) {
      navigateToConversationByMatter({ matterId: urlDerivedMatterId })
    }
  }, [chatId, chatFeature, isValidChatFeature, urlDerivedMatterId, initialLoadComplete, navigateToConversationByMatter])

  /**
   * When a chatId is present in the URL (currently viewing a conversation) and the active matter changes,
   * run matter-specific navigation logic.
   *
   * This handles transitions between matters by navigating to the last viewed conversation
   * (or most recent conversation) in the target matter.
   */
  useEffect(() => {
    if (
      chatId && // A chat is currently being viewed
      isValidChatFeature && // Matter filtering is enabled for this chat feature
      initialLoadComplete // The matter's conversations and the matter list are loaded
    ) {
      // Skip the first hydration — just record the initial value without triggering navigation
      if (prevActiveMatterId.current === undefined) {
        prevActiveMatterId.current = activeMatterId
        return
      }

      // Only navigate when the active matter actually changes after hydration
      if (activeMatterId !== prevActiveMatterId.current) {
        handleMatterChangeNavigation(activeMatterId)
        prevActiveMatterId.current = activeMatterId
      }
    }
  }, [chatId, activeMatterId, chatFeature, isValidChatFeature, initialLoadComplete, handleMatterChangeNavigation])

  /**
   * Set the active conversation id to keep track of the conversation currently being viewed
   */
  useEffect(() => {
    if (!chatId) return
    dispatch(setActiveConversationId({ conversationId: chatId }))
    return () => {
      dispatch(setActiveConversationId({ conversationId: null }))
    }
  }, [chatId, dispatch])

  /**
   * Syncs the content viewer state with the active content viewer for agent conversations.
   * - If no active content is found, clears the content viewer.
   * - Otherwise, sets the content viewer with the active content.
   */
  useEffect(() => {
    if (!AgentConversationActiveContentViewer) {
      dispatch(ContentViewerActions.clearContentViewer())
    } else {
      dispatch(ContentViewerActions.setContentViewerSession({ session: AgentConversationActiveContentViewer }))
    }
  }, [AgentConversationActiveContentViewer, dispatch])

  /**
   * Debounce the showing of the 404 chat not found error
   * This prevents a brief flash of the 404 screen during brief loading gaps (e.g. when a conversation is deleted)
   */
  useEffect(() => {
    if (isInvalidChatId) {
      const id = setTimeout(() => setShow404(true), 800)
      return () => clearTimeout(id)
    }
    setShow404(false)
  }, [isInvalidChatId])

  /**
   * Use effect to clear the content viewer when the component unmounts
   */
  useEffect(() => {
    return () => {
      dispatch(ContentViewerActions.clearContentViewer())
    }
  }, [dispatch])

  // IF INVALID FEATURE: show 404 error
  if (!isValidChatFeature || !chatFeature) {
    console.warn(`Invalid chat. Feature: ${chatFeature}, ID: ${chatId}`)

    // Return 404 error component and show home link if the feature doesn't exist
    return <ErrorComponent code={'404'} title={'Not Found'} message={"We can't find what you're looking for."} showHomeLink={!isValidChatFeature} />
  }

  if (!chatId) {
    // The matter is unassigned and there are no chats left on the matter - show the empty state
    if (initialLoadComplete && isUnassignedMatter(urlMatter) && !hasConversationsForMatter) {
      return (
        <div className="flex w-full h-full p-4 lg:px-28">
          <div className="flex flex-1 items-center justify-center rounded-md border border-brand-neutral-200 bg-brand-neutral-100">
            <div className="flex flex-col items-center text-center px-2 py-2">
              <p className="text-sm text-brand-neutral-600">No unassigned chats to file</p>
              <Link
                to={kMattersRoute}
                className="text-sm font-medium text-brand-700 underline transition-colors duration-150 hover:text-brand-800 focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-500"
              >
                Back to all matters
              </Link>
            </div>
          </div>
        </div>
      )
    }
    // Otherwise, we're still loading the chat list and matters - show a loading indicator
    return (
      <div className="flex items-center justify-center w-full h-full">
        <CircularProgress size={24} thickness={5} />
      </div>
    )
  }

  // console.log(`Rendering ${chatFeature} conversation id: ${chatId}`)

  return (
    <>
      {/* Display 1 or 2 columns depending on whether showReference is true and screen size */}
      {/* PDF Renderer uses 2/5 : 3/5 split, otherwise 1/2 : 1/2 */}
      <div className={`flex min-w-0 flex-grow gap-x-2`}>
        <div className="w-1/2 min-w-0 mx-0 lg:mx-0 flex-grow flex justify-center">
          {showAgentChat && <AgentChatWindow conversationId={chatId} />}
          {/* Show error if the chat id is not supported in the dashboard runtime */}
          {show404 && <ErrorComponent code={'404'} title={'Chat not found'} message={`We could not find the chat with id: ${chatId}`} />}
        </div>

        {/* Content viewer as second column - large screens */}
        {screenSize !== ChatBreakpoints.SMALL && (
          <CSSTransition
            appear
            nodeRef={referenceColumnRef}
            in={showContentViewerPane}
            timeout={{ enter: 200, exit: 0 }}
            classNames="content-viewer-column"
            unmountOnExit
          >
            <div ref={referenceColumnRef} className="w-1/2 min-w-0 flex flex-grow overflow-hidden">
              <ContentViewerColumn conversationId={chatId} />
            </div>
          </CSSTransition>
        )}

        {/* Content viewer as slideover - small screens */}
        {screenSize === ChatBreakpoints.SMALL && <ContentViewerSlideover open={showContentViewerPane} conversationId={chatId} />}
      </div>
    </>
  )
}
